import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { select, Store } from "@ngrx/store";
import dayjs from 'dayjs';
import { Observable, Subscription, throwError } from 'rxjs';
import { saveAs } from "file-saver";

import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import {
	AnamneseState,
	selectAnamneseState,
	AnamneseFileInfo
} from "libs/anamnese/src/lib/store/anamnese.reducer";
import {
	CaseAssessmentData,
	FollowupPackageType,
	FunctionALTreatmentPackages,
	FunctionALPackageType,
	QuotationModel,
	TreatmentModel,
	ProfessionalTreatmentPackages,
	TreatmentStatus,
	UpgradeModel,
	ExportTreatmentsQueryParams,
	TreatmentDashboardReportResponse,
	TreatmentDateType
} from "../models";
import { ProductKey, PROFESSIONAL_PRODUCT_KEYS, ToothArea } from "libs/product/src/lib/models/product.model";
import { MessageModel } from "libs/messages/src/lib/models/message.model";
import { UpsPickupAddress, UpsPickupContext, UpsPickupDateTime, UpsPickupInfo, UpsPickupReference } from "libs/tm-common/src/lib/models/ups.model";
import { CreateWebviewerDto, UpdateWebviewerDto, WebviewerDocument } from "libs/web-viewer/src/lib/models/webviewer-tms-api.model";
import { ErrorType } from "libs/tm-common/src/lib/models/error-type.model";
import { ReleaseData } from "libs/anamnese/src/lib/models/anamnese.model";
import { FunctionALPackagesService } from "./functional-packages.lib.service";

@Injectable({
	providedIn: 'root'
})
export class TreatmentService {
	private http = inject(HttpClient);
	private store = inject<Store<AnamneseState>>(Store);
	private config = inject<AppConfig>(APP_CONFIG_TOKEN);


	anamnese: AnamneseState;

	constructor() {

		this.store.pipe(select(selectAnamneseState))
			.subscribe(ae => this.anamnese = ae);
	}


	getCustomerTreatments() : Observable<TreatmentModel[]>{
		return this.http.get<TreatmentModel[]>(`${this.config.apiUrl}/treatments/customer`, {withCredentials: true});
	}

	getTreatment(treatmentId: string) : Observable<TreatmentModel>{
		return this.http.get<TreatmentModel>(`${this.config.apiUrl}/treatments/${treatmentId}`, {withCredentials: true});
	}

	getTreatmentsAdminPaginated(
		skip: number,
		limit: number,
		productFilter: string[],
		textFilter: string,
		statusFilter: string[],
		caseworkerFilter: string[],
		fromDateFilter: Date,
		activeSortFilter: string,
		sortDirection: string
		): Observable<{treatments: TreatmentModel[], count: number}>{
		const params = {
			skip: skip,
			limit: limit,
		};

		if (productFilter.length) {
			params["productFilter[]"] = productFilter;
		}
		if(textFilter){
			params["textFilter"] = textFilter;
		}
		if(statusFilter.length){
			params["statusFilter[]"] = statusFilter;
		}
		if(caseworkerFilter.length){
			params["caseworkerFilter[]"] = caseworkerFilter;
		}
		if(fromDateFilter){
			params["fromDateFilter"] = dayjs(fromDateFilter).format('YYYY-MM-DD');
		}
		if(activeSortFilter && sortDirection){
			params["activeSortFilter"] = activeSortFilter;
			params["sortDirection"] = sortDirection;
		}

		const options = {
			params: params,
			withCredentials: true
		};
		return this.http.get<{treatments: TreatmentModel[], count: number}>( `${this.config.apiUrl}/treatments/admin/paginated`, options);
	}

	getTreatmentDashboardSummary(
		productFilter: string[],
		startDateFilter: Date,
		endDateFilter: Date,
		dateType: TreatmentDateType,
		statusFilter: TreatmentStatus[]
	): Observable<TreatmentDashboardReportResponse> {
		const params = {
			...(startDateFilter && { startDateFilter: startDateFilter?.toISOString() }),
			...(endDateFilter && { endDateFilter: endDateFilter?.toISOString() }),
			...((startDateFilter || endDateFilter) && dateType && { dateType: dateType }),
			...(productFilter.length && { "productFilter[]": productFilter }),
			...(statusFilter.length && { "statusFilter[]": statusFilter }),
		};

		const options = {
			params: params,
			withCredentials: true,
		};
		return this.http.get<TreatmentDashboardReportResponse>(
			`${this.config.apiUrl}/treatments/admin/dashboard/report`,
			options
		);
	}

	getTreatmentsTmOrmco(fromDate?: Date): Observable<{ treatments: TreatmentModel[] }> {
		let params = {};
		if (fromDate) {
			// As it might be a considerable offset to Germany, we remove any timezone offset.
			// Code stolen from https://stackoverflow.com/a/29774197.
			const offset = fromDate.getTimezoneOffset() * 60 * 1000;
			const fromDateNoOffset = new Date(fromDate.getTime() - offset);
			const fromDateParam = fromDateNoOffset.toISOString().split("T")[0];

			params = { from: fromDateParam };
		}

		const options = {
			params: params,
			withCredentials: true
		};

		return this.http.get<{ treatments: TreatmentModel[] }>(`${this.config.apiUrl}/treatments/tmormco`, options);
	}

	createTreatment(): Observable<TreatmentModel> {
		const mediaIdsToUpdate: string[] = this.getMediaIdsToUpdate();
		const createTreatmentDto = this.buildCreateTreatmentDto(mediaIdsToUpdate);
		return this.http.post<TreatmentModel>(`${this.config.apiUrl}/treatments`, createTreatmentDto, {withCredentials: true});

	}

	createAssessmentTreatment(comment?: string): Observable<TreatmentModel> {
		const mediaIdsToUpdate: string[] = this.getMediaIdsToUpdate();
		const createAssessmentTreatmentDto = this.buildCreateAssessmentTreatmentDto(mediaIdsToUpdate, comment);
		return this.http.post<TreatmentModel>(`${this.config.apiUrl}/treatments/assessment`, createAssessmentTreatmentDto, {withCredentials: true});
	}

	updateCaseAssessmentTreatmentWithQuotation(): Observable<TreatmentModel> {
		const mediaIdsToUpdate: string[] = this.getMediaIdsToUpdate()
		const updateCaseAssessmentTreatmentDto = this.buildUpdateCaseAssessmentTreatmentDto(mediaIdsToUpdate);
		return this.http.post<TreatmentModel>(`${this.config.apiUrl}/treatments/${this.anamnese.caseAssessmentTreatment._id}/assessment`, updateCaseAssessmentTreatmentDto, {withCredentials: true});

	}

	updateTreatment(treatmentId: string, diff: Partial<TreatmentModel> | { quotation: Partial<QuotationModel> }): Observable<TreatmentModel> {
		return this.http.put<TreatmentModel>(`${this.config.apiUrl}/treatments/${treatmentId}`, diff, {withCredentials: true}); // XXX should be PATCH
	}

	submitReleaseRequest(
		treatmentId: string,
		releaseRequest: {
			releaseData: ReleaseData;
			totalPlannedSteps: number;
			upgrade?: UpgradeModel;
			language?: "de" | "en";
		}
	): Observable<{
		treatment: TreatmentModel;
		message: MessageModel;
		modification?: WebviewerDocument;
		modificationAlternativeOption?: WebviewerDocument;
	}> {
		return this.http.post<{
			treatment: TreatmentModel;
			message: MessageModel;
			modification?: WebviewerDocument;
			modificationAlternativeOption?: WebviewerDocument;
		}>(`${this.config.apiUrl}/treatments/${treatmentId}/releaseRequest`, releaseRequest, {
			withCredentials: true,
		});
	}

	submitReleaseRequestFunctionAL(
		treatmentId: string,
		language: "de" | "en" = "en"
	): Observable<{ treatment: TreatmentModel; message: MessageModel; modification?: WebviewerDocument }> {
		return this.http.post<{
			treatment: TreatmentModel;
			message: MessageModel;
			modification?: WebviewerDocument;
		}>(
			`${this.config.apiUrl}/treatments/${treatmentId}/releaseRequest/functional`,
			{ language: language },
			{ withCredentials: true }
		);
	}

	private getMediaIdsToUpdate() : string[]{
		const isFunctionALProduct = this.anamnese.product?.key === ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT;
		if(!isFunctionALProduct){
			const uploadedFiles = this.anamnese.uploadedFiles;
			return [
				uploadedFiles.uj,
				uploadedFiles.lj,
				...uploadedFiles.additionals]
				.filter(item => item !== null)
				.map((item: AnamneseFileInfo) => item.mediaId);
		}else{
			const uploadedFiles = this.anamnese.uploadedFilesFunctionAL;
			return [
				...uploadedFiles.jaws,
				...uploadedFiles.extraorals,
				...uploadedFiles.intraorals,
				...uploadedFiles.miscellaneous]
				.filter(item => item !== null)
				.map((item: AnamneseFileInfo) => item.mediaId);
		}
	}

	updateProfessionalPackages(treatmentId: string, totalSteps: number): Observable<{treatment: TreatmentModel, errors: ErrorType[]}>{
		const payload = {
			totalSteps: totalSteps
		}
		return this.http.patch<{treatment: TreatmentModel, errors: ErrorType[]}>(`${this.config.apiUrl}/treatments/${treatmentId}/packages/professional`, payload, {withCredentials: true});
	}

	updateFunctionALPackages(treatmentId: string, totalSteps: number): Observable<TreatmentModel>{
		const payload = {
			totalSteps: totalSteps
		}
		return this.http.patch<TreatmentModel>(`${this.config.apiUrl}/treatments/${treatmentId}/packages/functional`, payload, {withCredentials: true});
	}

	updateFunctionALProduct(treatmentId: string, productKey: ProductKey, totalSteps: number): Observable<TreatmentModel>{
		const payload = {
			productKey: productKey,
			totalSteps: totalSteps
		}
		return this.http.put<TreatmentModel>(`${this.config.apiUrl}/treatments/${treatmentId}/product/functional`, payload, {withCredentials: true});
	}

	updateProfessionalProduct(treatmentId: string, productKey: ProductKey, uj: boolean, lj: boolean): Observable<TreatmentModel>{
		const payload = {
			productKey: productKey,
			uj: uj,
			lj: lj
		}
		return this.http.put<TreatmentModel>(`${this.config.apiUrl}/treatments/${treatmentId}/product/professional`, payload, {withCredentials: true});
	}

	updateRetainerProduct(
		treatmentId: string,
		productKey: ProductKey
	): Observable<{ treatment: TreatmentModel; message: MessageModel }> {
		const payload = { productKey };
		return this.http.put<{
			treatment: TreatmentModel;
			message: MessageModel;
		}>(`${this.config.apiUrl}/treatments/${treatmentId}/product/retainer`, payload, {
			withCredentials: true,
		});
	}

	splitRetainerProduct(
		treatmentId: string,
		dto: {
			splitProductKey: ProductKey;
			splitUj: ToothArea;
			splitLj: ToothArea;
			addShippingCostsToSplitTreatment: boolean;
			remainingProductKey: ProductKey;
			remainingUj: ToothArea;
			remainingLj: ToothArea;
		}
	): Observable<{
		sourceTreatment: TreatmentModel;
		childTreatment: TreatmentModel;
		messages: MessageModel[];
	}> {
		return this.http.post<{
			sourceTreatment: TreatmentModel;
			childTreatment: TreatmentModel;
			messages: MessageModel[];
		}>(`${this.config.apiUrl}/treatments/${treatmentId}/product/split`, dto, {
			withCredentials: true,
		});
	}

	updateProductOptions(treatmentId: string, toothAreaUj: ToothArea, toothAreaLj: ToothArea): Observable<TreatmentModel>{
		const payload = {
			toothAreaUj: toothAreaUj,
			toothAreaLj: toothAreaLj
		}
		return this.http.patch<TreatmentModel>(`${this.config.apiUrl}/treatments/${treatmentId}/product/options`, payload, {withCredentials: true});
	}

	createUpsPickupRequest(treatmentId: string, address: UpsPickupAddress, dateTime: UpsPickupDateTime, context: UpsPickupContext, specialInstruction: string) {
		return this.http.post(
			`${this.config.apiUrl}/treatments/${treatmentId}/upsPickupRequest`,  {address, dateTime, context, specialInstruction}, {withCredentials: true}
		);
	}

	submitComplaint(treatmentId: string, comment: string, complaintReason: string, hasUserUploadedFiles: boolean, language: "de" | "en"): Observable<TreatmentModel>{
		const payload = {
			comment,
			complaintReason,
			hasUserUploadedFiles,
			language
		}
		try{
			return this.http.post<TreatmentModel>(
				`${this.config.apiUrl}/treatments/${treatmentId}/complaint`,  payload, {withCredentials: true}
			);
		}catch(error){
			console.log(JSON.stringify(error));
			// rethrow as rxjs error to allow handling via subscribe - error
			return throwError(() => new Error(`${error.status} ${error.message}`));
		}
	}

	submitCaseAssessment(treatmentId: string, caseAssessmentData: CaseAssessmentData): Observable<TreatmentModel>{
		return this.http.post<TreatmentModel>(`${this.config.apiUrl}/treatments/${treatmentId}/assessment/submit`, caseAssessmentData, {withCredentials: true});
	}

	rejectCaseAssessmentRequest(treatmentId: string): Observable<TreatmentModel>{
		return this.http.post<TreatmentModel>(`${this.config.apiUrl}/treatments/${treatmentId}/assessment/reject`, {withCredentials: true});
	}

	canUserOrderDiscountedMemotain(_id: string) : Observable<{ujAvailable: boolean, ljAvailable: boolean}> {
		return this.http.get<{ujAvailable: boolean, ljAvailable: boolean}>(`${this.config.apiUrl}/treatments/${_id}/memotain/discounted/quota`, {withCredentials: true});
	}

	createWebviewerModification(
		treatmentId: string,
		modification: CreateWebviewerDto,
		webviewerUrl: string,
		language: "de" | "en" = "en"
	): Observable<{ treatment: TreatmentModel; message?: MessageModel; modification: WebviewerDocument }> {
		return this.http.post<{
			treatment: TreatmentModel;
			message?: MessageModel;
			modification: WebviewerDocument;
		}>(
			`${this.config.apiUrl}/treatments/${treatmentId}/webviewer/modification`,
			{ modification, webviewerUrl, language },
			{ withCredentials: true }
		);
	}

	exportTreatmentsAsCsv(params: ExportTreatmentsQueryParams): Subscription {
		const queryParams = {
			dateType: params.dateType,
		};

		if (params.customerId) {
			queryParams["customerId"] = params.customerId;
		}
		if (params.productFilter?.length) {
			queryParams["productFilter[]"] = params.productFilter;
		}
		if (params.statusFilter?.length) {
			queryParams["statusFilter[]"] = params.statusFilter;
		}
		if (params.caseworkerFilter?.length) {
			queryParams["caseworkerFilter[]"] = params.caseworkerFilter;
		}
		if (params.fromDateFilter) {
			queryParams["fromDateFilter"] = dayjs(params.fromDateFilter).format("YYYY-MM-DD");
		}
		if (params.toDateFilter) {
			queryParams["toDateFilter"] = dayjs(params.toDateFilter).format("YYYY-MM-DD");
		}

		const options = {
			params: queryParams,
			withCredentials: true,
			responseType: "text" as const,
			observe: "response" as const,
		};

		return this.http.get(`${this.config.apiUrl}/treatments/admin/export/csv`, options).subscribe({
			next: (response: HttpResponse<string>) => {
				const blob: Blob = new Blob([response.body], { type: "text/csv" });
				saveAs(blob, "output.csv");
			},
			error: (error: any) => {
				console.error("Error caught while exporting treatments as CSV", error);
			},
		});
	}

	updateWebviewerModification(
		treatmentId: string,
		modificationId: string,
		modification: UpdateWebviewerDto,
		language: "de" | "en" = "en"
	): Observable<{ treatment: TreatmentModel; message?: MessageModel; modification: WebviewerDocument }> {
		return this.http.patch<{
			treatment: TreatmentModel;
			message?: MessageModel;
			modification: WebviewerDocument;
		}>(
			`${this.config.apiUrl}/treatments/${treatmentId}/webviewer/modification`,
			{ modificationId, modification, language },
			{ withCredentials: true }
		);
	}

	////// ^^^^^^  API    ^^^^^ /////////////////////////////////////////////////////////////////////////////////
	////////////////////////////////////////////////////////////////////////////////////////////////////////////
	////// vvvvvv  Utils  vvvvv /////////////////////////////////////////////////////////////////////////////////

	private buildCreateTreatmentDto(mediaIdsToUpdate: string[]) {
		const createTreatmentDto = { //see CreateTreatmentDto
			patientId: this.anamnese.patientId,
			productKey: this.anamnese.product.key,
			uj: this.anamnese.product.options.uj ? this.anamnese.product.options.uj : ToothArea.NONE,
			lj: this.anamnese.product.options.lj ? this.anamnese.product.options.lj : ToothArea.NONE,
			questionnaire: this.anamnese.questionairePreparedAnswers ?? undefined,
			questionnaireFunctionAL: this.anamnese.questionnaireAnswersFunctionAL ?? undefined,
			questionnaireMichiganSplint: this.anamnese.questionnaireAnswersMichigan ?? undefined,
			dataTransfer: this.anamnese.product.options.dataTransfer,
			comment: this.anamnese.comment ? this.anamnese.comment.trim() : undefined,
			billingAddressCode: !this.anamnese.product.handling.hasUniqueBillingAddress
				? this.anamnese.product.handling.billing.AddressName
				: undefined,
			shippingType: this.anamnese.product.handling.shipment.Method,
			shippingAddressCode: !this.anamnese.product.handling.hasUniqueShippingAddress
				? this.anamnese.product.handling.shipping.AddressName
				: undefined,
			country: this.anamnese.product.handling.shipping.Country,
			mediaIds: mediaIdsToUpdate,
			parentTreatmentId: this.anamnese.parentTreatmentId ?? undefined,
			professionalPrintService: this.anamnese.professionalPrintService ?? undefined,
			benefitName: this.anamnese.selectedBenefitName?.name ?? undefined
		};
		return createTreatmentDto;
	}

	private buildCreateAssessmentTreatmentDto(mediaIdsToUpdate: string[], comment?: string) {
		const createTreatmentDto = { //see CreateAssessmentTreatmentDto
			patientId: this.anamnese.patientId,
			productKey: this.anamnese.product.key,
			uj: this.anamnese.product.options.uj ? this.anamnese.product.options.uj : ToothArea.NONE,
			lj: this.anamnese.product.options.lj ? this.anamnese.product.options.lj : ToothArea.NONE,
			questionnaire: this.anamnese.questionairePreparedAnswers ?? undefined,
			comment: comment ? comment.trim() : undefined,
			mediaIds: mediaIdsToUpdate,
		};
		return createTreatmentDto;
	}

	private buildUpdateCaseAssessmentTreatmentDto(mediaIdsToUpdate: string[]){
		const createTreatmentDto = { //see UpdateAssessmentTreatmentDto
			productKey: this.anamnese.product.key,
			dataTransfer: this.anamnese.product.options.dataTransfer,
			billingAddressCode: !this.anamnese.product.handling.hasUniqueBillingAddress
				? this.anamnese.product.handling.billing.AddressName
				: undefined,
			shippingType: this.anamnese.product.handling.shipment.Method,
			shippingAddressCode: !this.anamnese.product.handling.hasUniqueShippingAddress
				? this.anamnese.product.handling.shipping.AddressName
				: undefined,
			country: this.anamnese.product.handling.shipping.Country,
			uj: this.anamnese.product.options.uj ? this.anamnese.product.options.uj : ToothArea.NONE,
			lj: this.anamnese.product.options.lj ? this.anamnese.product.options.lj : ToothArea.NONE,
			comment: this.anamnese.comment ? this.anamnese.comment.trim() : undefined,
			mediaIds: mediaIdsToUpdate,
			benefitName: this.anamnese.selectedBenefitName?.name ?? undefined
		};
		return createTreatmentDto;
	}

	determineOrderablePackageTypeProfessional(treatment: TreatmentModel): FollowupPackageType {

		const packages: ProfessionalTreatmentPackages = treatment.packages as ProfessionalTreatmentPackages;
		if (!packages){
			return null;
		}
		if (!PROFESSIONAL_PRODUCT_KEYS.includes(treatment.product.key)) {
			return null;
		}
		if (treatment.status !== TreatmentStatus.Shipped &&
			treatment.status !== TreatmentStatus.ComplaintResolved ) {
			return null;
		}
		const noOfOrders = packages.next;

		// all product steps ordered ???
		if (noOfOrders >= packages.steps.length) {
			if (noOfOrders >= packages.steps.length + packages.freeCorrections.length) {
				return FollowupPackageType.ChargeableRefinement;
			} else {
				return FollowupPackageType.Refinement;
			}
		} else {
			return FollowupPackageType.Package;
		}
	}

	determineOrderablePackageTypeFunctionAL(treatment: TreatmentModel): FollowupPackageType {
		const packages: FunctionALTreatmentPackages = treatment.packages as FunctionALTreatmentPackages;
		if (!packages){
			return null;
		}
		if (treatment.status !== TreatmentStatus.Shipped &&
			treatment.status !== TreatmentStatus.ComplaintResolved ) {
			return null;
		}

		if(packages.pending?.length > 0 && packages.pending.at(0).type === FunctionALPackageType.PLANNED) {
			return FollowupPackageType.Package;
		}
		if(treatment.product.key === ProductKey.FUNCTIONAL_SCHIENE_MAX) {
			return FollowupPackageType.Refinement;
		}
		if([ProductKey.FUNCTIONAL_SCHIENE_NEO, ProductKey.FUNCTIONAL_SCHIENE_PLUS].includes(treatment.product.key)) {
			return FunctionALPackagesService.hasRefinementPackageConsumed(packages) ?
				FollowupPackageType.ChargeableRefinement : FollowupPackageType.Refinement ;
		}
		return FollowupPackageType.Refinement;
	}

	checkPickUpInfo(pr: UpsPickupReference) : UpsPickupInfo {
		const UPS_DATE_FORMAT = 'YYYYMMDD';
		const UPS_TIME_FORMAT = 'hhmm';

		let isTimeSlotActive = false;
		let timeUntilSlotIsActive = 0;
		let isExpired = false;

		// get prepared dates
		const ready = dayjs(
			pr.dateTime.PickupDate + pr.dateTime.ReadyTime,
			UPS_DATE_FORMAT + UPS_TIME_FORMAT);
		const close = dayjs(
			pr.dateTime.PickupDate + pr.dateTime.CloseTime,
			UPS_DATE_FORMAT + UPS_TIME_FORMAT);

		// check if pickup request timeslot is currently active
		if (ready.diff(dayjs()) <= 0) {
			isTimeSlotActive = true;
		} else {
			timeUntilSlotIsActive = ready.diff(dayjs(), 'minutes');
		}

		// check if pickup request time is expired
		if (close.diff(dayjs()) <= 0) {
			isExpired = true;
			isTimeSlotActive = false;
		}

		return {
			isExpired: isExpired,
			isTimeSlotActive: isTimeSlotActive,
			timeUntilSlotIsActive: {
				hours: Math.floor(timeUntilSlotIsActive / 60 || 0),
				minutes: timeUntilSlotIsActive % 60 || 0,
			},
			address: pr.address,
			prn: pr.prn
		};
	}

	hasPendingUpsPickupRequest(treatment: TreatmentModel): boolean {
		let pickupInfo: UpsPickupInfo;
		if (treatment.upsPickupReferences?.length > 0) {
			pickupInfo = this.checkPickUpInfo(treatment.upsPickupReferences.at(-1));
		}

		if (!pickupInfo || pickupInfo.isExpired) {
			return false;
		}

		return true;
	}
}
